import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    computed,
    effect,
    inject,
    signal,
} from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";

import { Observable, Subscription } from "rxjs";

import { LayoutFacade } from "@hermes/aphrodite-layout-state";
import { type AboutMenu, type Category } from "@hermes/api-model-menu";
import {
    isAboutMenuEntryWithSubItems,
    isMenuEntryWithSubCategories,
} from "@hermes/domains-shell-fragments-menu-shared";
import { cleanMenuEntryName } from "@hermes/domains-shell-states-menu-state";

import {
    FeatureFlagFacade,
    HEADER_FULL_TRANSPARENT,
} from "@hermes/hermes-web-states-flipper-state";
import { stopEventPropagation } from "@hermes/utils-generic-helpers";
import { RouterService } from "@hermes/utils-generic-services-router";

import { MenuBarLinkComponent } from "../menu-bar-link/menu-bar-link.component";
import { activeMenu } from "../menu-bar.state";
import { MenuSubCategoryComponent } from "../sub-category/menu-sub-category.component";

@Component({
    selector: "h-menu-parent-category",
    imports: [MenuSubCategoryComponent, MenuBarLinkComponent],
    templateUrl: "./menu-parent-category.component.html",
    styleUrls: ["./menu-parent-category.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuParentCategoryComponent implements OnInit, OnDestroy {
    private router = inject(Router);
    private detector = inject(ChangeDetectorRef);
    private layoutFacade = inject(LayoutFacade);
    private featureFlagFacade = inject(FeatureFlagFacade);

    @ViewChild("menuEntryButton", { read: ElementRef })
    public menuEntryButton?: ElementRef<HTMLButtonElement>;
    @ViewChild("menuEntryLink", { read: MenuBarLinkComponent })
    public menuLinkComponent?: MenuBarLinkComponent;

    @Input()
    public menuEntry?: Category = undefined;

    @Input()
    public menuAboutEntry?: AboutMenu = undefined;

    @Input()
    public isFirstCategory: boolean = false;

    @Input()
    public isLastCategory: boolean = false;

    @Input({ required: true })
    public onCursorLeaveMenuBar$!: Observable<void>;

    public hoverEnterTimeout!: NodeJS.Timeout;
    public hoverLeaveTimeout!: NodeJS.Timeout;

    public activationOrigin = signal<"keyboard" | "mouse">("mouse");

    public isActive = signal(false);

    public isFocused = signal(false);

    public isMenuEntryWithSubCategories: boolean = false;

    public isAboutMenuEntryWithSubItems: boolean = false;

    public subscriptions: Subscription = new Subscription();

    public routerService = inject(RouterService);

    public currentPrimaryEntry = "";
    public isCurrentPrimaryEntryActive = false;

    public isHeaderTransparent = toSignal(
        this.layoutFacade.headerTransparent$,
        { initialValue: false },
    );

    public shouldBeTransparent = computed(
        () => !this.isActive() && !this.headerPositionIsSticky(),
    );

    public headerPositionIsSticky = toSignal(this.layoutFacade.isFixedMenu$, {
        initialValue: false,
    });

    public isHeaderFullTransparentFF = toSignal(
        this.featureFlagFacade.isActivated(HEADER_FULL_TRANSPARENT),
        { initialValue: false },
    );

    public isAnyParentCategoryActive = computed(() => {
        const active = activeMenu();
        return !!active && active.isActive();
    });

    public shouldShowWhiteText = computed(
        () =>
            this.isHeaderTransparent() &&
            this.isHeaderFullTransparentFF() &&
            !this.isAnyParentCategoryActive(),
    );

    constructor() {
        effect(() => {
            if (this.isFocused()) {
                if (this.isMenuEntryWithSubCategories && this.menuEntryButton) {
                    this.menuEntryButton.nativeElement.focus();
                } else if (
                    !this.isMenuEntryWithSubCategories &&
                    this.menuLinkComponent
                ) {
                    this.menuLinkComponent.getFocusableElement().focus();
                }

                if (this.isAboutMenuEntryWithSubItems && this.menuEntryButton) {
                    this.menuEntryButton.nativeElement.focus();
                } else if (
                    !this.isAboutMenuEntryWithSubItems &&
                    this.menuLinkComponent
                ) {
                    this.menuLinkComponent.getFocusableElement().focus();
                }
            }
        });

        effect(() => {
            const currentActiveMenu = activeMenu();
            if (
                this.activationOrigin() === "keyboard" &&
                currentActiveMenu !== this &&
                this.isActive()
            ) {
                this.isActive.set(false);
            }

            // Update header tranparency facade
            this.layoutFacade.setHeaderTransparency(this.shouldBeTransparent());
        });
    }

    public get menuEntryNameHtmlClean(): string {
        return cleanMenuEntryName(
            this.menuEntry?.name || this.menuAboutEntry?.label || "",
            true,
        );
    }

    @Input({ required: true })
    public set focused(focused: boolean) {
        this.isFocused.set(focused);
    }

    public ngOnInit(): void {
        this.isMenuEntryWithSubCategories = isMenuEntryWithSubCategories(
            this.menuEntry,
        );
        this.isAboutMenuEntryWithSubItems = isAboutMenuEntryWithSubItems(
            this.menuAboutEntry,
        );

        this.subscriptions.add(
            this.onCursorLeaveMenuBar$.subscribe(() => {
                this.isActive.set(false);
            }),
        );

        this.subscriptions.add(
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.currentPrimaryEntry = event.urlAfterRedirects
                        .replace(/#\|$/, "")
                        .split("/")[2];
                    this.updateIsCurrentPrimaryEntryActive();
                    this.detector.detectChanges();
                }
            }),
        );
    }

    public onMouseEnterParentCategory() {
        this.activationOrigin.set("mouse");
        this.isActive.set(true);
        activeMenu.set(this);
    }

    public onMouseLeaveParentCategory() {
        this.isActive.set(false);
        activeMenu.set(undefined);
    }

    public toggleEntry(event: Event): void {
        stopEventPropagation(event);
        this.activationOrigin.set("keyboard");

        if (this.isActive()) {
            this.isActive.set(false);
        } else {
            this.isActive.set(true);
            activeMenu.set(this);
            this.menuEntryButton?.nativeElement.focus();
        }
    }

    public closeMenu(
        isShiftKeyActiveFromParentCategory: boolean = false,
    ): void {
        this.isActive.set(false);
        activeMenu.set(this);
        if (!isShiftKeyActiveFromParentCategory) {
            if (this.isMenuEntryWithSubCategories && this.menuEntryButton) {
                this.menuEntryButton.nativeElement.focus();
            } else if (
                !this.isMenuEntryWithSubCategories &&
                this.menuLinkComponent
            ) {
                this.menuLinkComponent.getFocusableElement().focus();
            }
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    // a verifier le fonctionnement
    private updateIsCurrentPrimaryEntryActive(): void {
        const menuEntryPrimaryCategory =
            this.menuEntry?.categoryPath?.split("/")[4];
        let currentEditoSlug = "";

        if (
            this.menuEntry?.title &&
            this.menuEntry?.path &&
            this.menuEntry?.image &&
            Object.keys(this.menuEntry).length > 0
        ) {
            currentEditoSlug = this.menuEntry.path?.split("/")[4];
        }

        this.isCurrentPrimaryEntryActive =
            (Boolean(this.menuEntry?.categoryPath) &&
                this.currentPrimaryEntry === menuEntryPrimaryCategory) ||
            this.currentPrimaryEntry === currentEditoSlug;
    }
}
